"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { ArrowRight, ChevronLeft, ChevronRight, ExternalLink } from "lucide-react";

export type PlatformPromoSlideVariant = "promo" | "rental" | "airport" | "host" | "ad_slot";

export type PlatformPromoSlide = {
  id: string;
  variant: PlatformPromoSlideVariant;
  subtitle: string;
  title: string;
  line2?: string;
  description?: string;
  href: string;
  cta: string;
  /** Если задано — фон слайда как у рекламного баннера; иначе градиент по variant. */
  image_url?: string | null;
  /** Платный баннер из таблицы banners — уголок «реклама» (paidAdCornerLabel). */
  isPaidAd?: boolean;
};

function isExternalHref(href: string): boolean {
  return /^https?:\/\//i.test(href.trim());
}

const DURATION = 6500;

/**
 * Только longhand-свойства (backgroundImage / backgroundColor), без shorthand `background`,
 * иначе браузер разворачивает градиент в набор свойств и React ловит hydration mismatch.
 */
export function platformPromoSlideBackground(variant: PlatformPromoSlideVariant): React.CSSProperties {
  switch (variant) {
    case "promo":
      return {
        backgroundColor: "#090706",
        backgroundImage:
          "linear-gradient(135deg, #090706 0%, #7f1d1d 38%, #dc2626 72%, #E63946 100%)",
      };
    case "rental":
      return {
        backgroundColor: "#020617",
        backgroundImage:
          "linear-gradient(135deg, #020617 0%, #155e75 42%, #06b6d4 78%, #0891b2 100%)",
      };
    case "airport":
      return {
        backgroundColor: "#0f0d24",
        backgroundImage:
          "linear-gradient(135deg, #0f0d24 0%, #3730a3 48%, #6366f1 72%, #a5b4fc 100%)",
      };
    case "host":
      return {
        backgroundColor: "#022c22",
        backgroundImage:
          "linear-gradient(125deg, #022c22 0%, #064e3b 28%, #059669 58%, #10b981 82%, #34d399 100%)",
      };
    case "ad_slot":
      return {
        backgroundColor: "#0a0a0a",
        backgroundImage:
          "linear-gradient(128deg, #0c0a09 0%, #27272a 40%, #3f3f46 68%, rgba(127, 29, 29, 0.55) 100%)",
      };
    default:
      return { backgroundColor: "#111827" };
  }
}

function variantEyebrowAccent(variant: PlatformPromoSlideVariant): string {
  switch (variant) {
    case "promo":
      return "bg-[#E63946]";
    case "rental":
      return "bg-cyan-400";
    case "airport":
      return "bg-indigo-300";
    case "host":
      return "bg-amber-400 shadow-[0_0_12px_rgba(251,191,36,0.55)]";
    case "ad_slot":
      return "bg-[#E63946] shadow-[0_0_14px_rgba(230,57,70,0.55)]";
    default:
      return "bg-white/70";
  }
}

function ctaClassForVariant(variant: PlatformPromoSlideVariant): string {
  switch (variant) {
    case "host":
      return "pointer-events-auto mt-3 sm:mt-4 inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-400 px-4 sm:px-6 py-2.5 sm:py-3 text-sm font-extrabold text-emerald-950 shadow-[0_4px_24px_rgba(251,191,36,0.45),0_0_40px_rgba(16,185,129,0.25)] ring-2 ring-amber-200/60 transition-all hover:brightness-105 hover:shadow-[0_6px_32px_rgba(251,191,36,0.55)] active:scale-[0.97]";
    case "ad_slot":
      return "pointer-events-auto mt-3 sm:mt-4 inline-flex items-center gap-2 rounded-xl bg-[#E63946] px-4 sm:px-6 py-2.5 sm:py-3 text-sm font-bold text-white shadow-[0_4px_28px_rgba(230,57,70,0.5)] ring-2 ring-white/35 transition-all hover:bg-[#c1121f] active:scale-[0.97]";
    default:
      return "pointer-events-auto mt-3 sm:mt-4 inline-flex items-center gap-1.5 rounded-xl bg-white px-3.5 sm:px-5 py-2 sm:py-2.5 text-xs sm:text-sm font-semibold text-gray-900 shadow-lg ring-1 ring-white/20 transition-all hover:bg-gray-100 active:scale-95";
  }
}

export default function HomePlatformPromoCarousel({
  slides,
  cornerLabel,
  paidAdCornerLabel,
}: {
  slides: PlatformPromoSlide[];
  cornerLabel: string;
  /** Подпись в углу для слайдов isPaidAd (например «реклама»). */
  paidAdCornerLabel?: string;
}) {
  const [current, setCurrent] = useState(0);
  const [prevIdx, setPrevIdx] = useState<number | null>(null);
  const [paused, setPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const [textKey, setTextKey] = useState(0);

  const rafRef = useRef<number | null>(null);
  const elapsedRef = useRef(0);
  const lastTickRef = useRef<number | null>(null);
  const touchStartX = useRef<number | null>(null);
  const count = slides.length;

  const goTo = useCallback(
    (idx: number) => {
      const next = (idx + count) % count;
      setPrevIdx(current);
      setCurrent(next);
      setTextKey((k) => k + 1);
      elapsedRef.current = 0;
      setProgress(0);
      setTimeout(() => setPrevIdx(null), 700);
    },
    [count, current],
  );

  const nextSlide = useCallback(() => goTo(current + 1), [current, goTo]);
  const prevSlide = useCallback(() => goTo(current - 1), [current, goTo]);

  useEffect(() => {
    if (count <= 1) return;
    const tick = (ts: number) => {
      if (!paused) {
        if (lastTickRef.current !== null) elapsedRef.current += ts - lastTickRef.current;
        lastTickRef.current = ts;
        setProgress(Math.min((elapsedRef.current / DURATION) * 100, 100));
        if (elapsedRef.current >= DURATION) {
          elapsedRef.current = 0;
          lastTickRef.current = null;
          setPrevIdx(current);
          setCurrent((c) => (c + 1) % count);
          setTextKey((k) => k + 1);
          setProgress(0);
          setTimeout(() => setPrevIdx(null), 700);
        }
      } else {
        lastTickRef.current = null;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [paused, count, current]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") prevSlide();
      if (e.key === "ArrowRight") nextSlide();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [prevSlide, nextSlide]);

  useEffect(() => {
    const onVisibility = () => setPaused(document.hidden);
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  if (!count) return null;

  /** Только активный и уходящий слайд — иначе next dev помечает LCP как lazy (last write wins). */
  const heroSlideIndices = useMemo(() => {
    const idx = new Set<number>([current]);
    if (prevIdx !== null) idx.add(prevIdx);
    return Array.from(idx).sort((a, b) => {
      if (a === current) return 1;
      if (b === current) return -1;
      return a - b;
    });
  }, [current, prevIdx]);

  const slide = slides[current];

  return (
    <div
      className="relative w-full overflow-hidden select-none bg-gray-950"
      style={{ height: "clamp(200px, 56vw, 560px)" }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={(e) => {
        touchStartX.current = e.touches[0].clientX;
      }}
      onTouchEnd={(e) => {
        if (touchStartX.current === null) return;
        const delta = e.changedTouches[0].clientX - touchStartX.current;
        if (Math.abs(delta) > 40) delta < 0 ? nextSlide() : prevSlide();
        touchStartX.current = null;
      }}
    >
      {slides.map((s, i) => {
        const active = i === current;
        const isLeaving = i === prevIdx;
        const img = s.image_url?.trim();
        const mountImage = img && heroSlideIndices.includes(i);
        return (
          <div
            key={s.id}
            className="absolute inset-0"
            style={{
              opacity: active ? 1 : isLeaving ? 0 : 0,
              zIndex: active ? 10 : isLeaving ? 5 : 0,
              transition: "opacity 0.7s cubic-bezier(0.4,0,0.2,1)",
              ...(img ? {} : platformPromoSlideBackground(s.variant)),
            }}
          >
            {mountImage ? (
              active ? (
                <Image
                  src={img}
                  alt={s.title}
                  fill
                  sizes="100vw"
                  className="object-cover"
                  style={{
                    transform: active ? "scale(1.05)" : "scale(1)",
                    transition: active ? `transform ${DURATION}ms linear` : "none",
                  }}
                  priority={i === 0 && active}
                  loading={i === 0 && active ? "eager" : "lazy"}
                  fetchPriority={i === 0 && active ? "high" : "auto"}
                  decoding="async"
                  draggable={false}
                />
              ) : (
                // eslint-disable-next-line @next/next/no-img-element -- уходящий слайд вне LCP-карты next/image
                <img
                  src={img}
                  alt={s.title}
                  className="absolute inset-0 h-full w-full object-cover"
                  style={{
                    transform: "scale(1)",
                    transition: "none",
                  }}
                  loading="lazy"
                  decoding="async"
                  draggable={false}
                />
              )
            ) : !img ? (
              <>
                <div
                  className="absolute inset-0 opacity-35"
                  style={{
                    background:
                      "radial-gradient(ellipse 75% 55% at 88% 8%, rgba(255,255,255,0.22), transparent)",
                  }}
                />
                {s.variant === "host" ? (
                  <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    <div
                      className="absolute -left-[20%] top-1/4 h-[120%] w-[70%] -rotate-12 rounded-[100%] bg-amber-400/10 blur-3xl platform-host-glow-a"
                    />
                    <div
                      className="absolute -right-[10%] -bottom-[20%] h-[85%] w-[60%] rounded-full bg-emerald-300/15 blur-3xl platform-host-glow-b"
                    />
                    <div className="pointer-events-none absolute inset-0 overflow-hidden">
                      <div className="platform-host-shine-bar absolute top-0 h-full w-[45%] -skew-x-12 bg-gradient-to-r from-transparent via-white/15 to-transparent opacity-70" />
                    </div>
                  </div>
                ) : s.variant === "ad_slot" ? (
                  <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute inset-3 sm:inset-5 rounded-2xl border-2 border-dashed border-white/30 sm:border-white/40" />
                    <div className="absolute left-1/2 top-[18%] h-[40%] w-[min(92%,520px)] -translate-x-1/2 rounded-3xl bg-[#E63946]/12 blur-3xl" />
                  </div>
                ) : null}
              </>
            ) : null}
          </div>
        );
      })}

      <div className="absolute inset-0 z-20 pointer-events-none">
        <div
          className="absolute inset-0"
          style={{
            background:
              slide.variant === "host"
                ? "linear-gradient(to top, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.5) 32%, rgba(2,44,34,0.12) 62%, transparent 100%)"
                : slide.variant === "ad_slot"
                  ? "linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.52) 34%, rgba(0,0,0,0.12) 62%, transparent 100%)"
                  : "linear-gradient(to top, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.4) 35%, rgba(0,0,0,0.05) 65%, transparent 100%)",
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              slide.variant === "host"
                ? "linear-gradient(to right, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.2) 45%, transparent 60%)"
                : slide.variant === "ad_slot"
                  ? "linear-gradient(to right, rgba(0,0,0,0.62) 0%, rgba(0,0,0,0.28) 45%, transparent 58%)"
                  : "linear-gradient(to right, rgba(0,0,0,0.45) 0%, transparent 55%)",
          }}
        />
      </div>

      <div className="absolute inset-0 z-30 flex items-end pointer-events-none">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-8 pb-4 sm:pb-8 flex items-end justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div
              key={`sub-${textKey}`}
              className="inline-flex items-center gap-2 mb-2 sm:mb-3"
              style={{
                animation: "platformSlideUp 0.5s cubic-bezier(0.22,1,0.36,1) both",
              }}
            >
              <span className={`w-4 sm:w-5 h-[2px] rounded-full ${variantEyebrowAccent(slide.variant)}`} />
              <span
                className={`text-[10px] sm:text-[11px] font-bold tracking-[0.15em] sm:tracking-[0.2em] uppercase ${
                  slide.variant === "host"
                    ? "text-amber-100/95 drop-shadow-[0_0_12px_rgba(251,191,36,0.35)]"
                    : slide.variant === "ad_slot"
                      ? "text-amber-100/90 drop-shadow-[0_0_12px_rgba(230,57,70,0.28)]"
                      : "text-white/70"
                }`}
              >
                {slide.subtitle}
              </span>
              <span className={`w-4 sm:w-5 h-[2px] rounded-full ${variantEyebrowAccent(slide.variant)}`} />
            </div>

            <h2
              key={`title-${textKey}`}
              className={`leading-[1.15] line-clamp-3 ${
                slide.variant === "host"
                  ? "text-white font-extrabold drop-shadow-[0_4px_24px_rgba(0,0,0,0.75)] [text-shadow:0_0_40px_rgba(16,185,129,0.35)]"
                  : slide.variant === "ad_slot"
                    ? "text-white font-extrabold drop-shadow-lg [text-shadow:0_0_36px_rgba(230,57,70,0.4)]"
                    : "text-white font-bold drop-shadow-lg"
              }`}
              style={{
                fontSize:
                  slide.variant === "host"
                    ? "clamp(1.2rem, 5vw, 2.75rem)"
                    : slide.variant === "ad_slot"
                      ? "clamp(1.12rem, 4.8vw, 2.55rem)"
                      : "clamp(1.05rem, 4.2vw, 2.35rem)",
                textShadow:
                  slide.variant === "host"
                    ? "0 3px 28px rgba(0,0,0,0.65), 0 1px 0 rgba(255,255,255,0.12)"
                    : slide.variant === "ad_slot"
                      ? "0 3px 26px rgba(0,0,0,0.7), 0 0 28px rgba(230,57,70,0.25)"
                      : "0 2px 24px rgba(0,0,0,0.6)",
                animation: "platformSlideUp 0.55s 0.07s cubic-bezier(0.22,1,0.36,1) both",
              }}
            >
              {slide.title}
            </h2>

            {slide.line2 ? (
              <p
                key={`line2-${textKey}`}
                className="mt-2 sm:mt-3 inline-block rounded-lg bg-black/35 px-3 py-1.5 font-mono text-sm text-white/95 backdrop-blur-sm border border-white/10"
                style={{
                  animation: "platformSlideUp 0.58s 0.1s cubic-bezier(0.22,1,0.36,1) both",
                }}
              >
                {slide.line2}
              </p>
            ) : null}

            {slide.description ? (
              <p
                key={`desc-${textKey}`}
                className={`mt-2 sm:mt-3 max-w-xl leading-relaxed ${
                  slide.variant === "host"
                    ? "max-w-lg text-sm sm:text-lg text-white/92 font-medium sm:leading-snug"
                    : slide.variant === "ad_slot"
                      ? "max-w-lg text-sm sm:text-base text-white/88 font-medium"
                      : "text-sm sm:text-base text-white/80"
                }`}
                style={{
                  animation: "platformSlideUp 0.58s 0.1s cubic-bezier(0.22,1,0.36,1) both",
                }}
              >
                {slide.description}
              </p>
            ) : null}

            {slide.cta.trim() && slide.href.trim() ? (
              isExternalHref(slide.href) ? (
                <a
                  key={`cta-${textKey}`}
                  href={slide.href}
                  target="_blank"
                  rel="noreferrer"
                  className={ctaClassForVariant(slide.variant)}
                  style={{
                    animation: "platformSlideUp 0.6s 0.14s cubic-bezier(0.22,1,0.36,1) both",
                  }}
                >
                  {slide.cta}{" "}
                  {slide.isPaidAd ? (
                    <ExternalLink size={14} className="flex-shrink-0 opacity-90" />
                  ) : (
                    <ArrowRight
                      size={slide.variant === "host" ? 18 : slide.variant === "ad_slot" ? 17 : 14}
                      className="flex-shrink-0"
                    />
                  )}
                </a>
              ) : (
                <Link
                  key={`cta-${textKey}`}
                  href={slide.href}
                  className={ctaClassForVariant(slide.variant)}
                  style={{
                    animation: "platformSlideUp 0.6s 0.14s cubic-bezier(0.22,1,0.36,1) both",
                  }}
                >
                  {slide.cta}{" "}
                  <ArrowRight
                    size={slide.variant === "host" ? 18 : slide.variant === "ad_slot" ? 17 : 14}
                    className="flex-shrink-0"
                  />
                </Link>
              )
            ) : null}
          </div>

          {count > 1 && (
            <div className="hidden sm:flex flex-col items-end gap-3 flex-shrink-0">
              <span className="text-white/40 text-xs font-mono tabular-nums">
                {String(current + 1).padStart(2, "0")} / {String(count).padStart(2, "0")}
              </span>
              <div className="flex items-center gap-2 pointer-events-auto">
                {slides.map((s, i) => {
                  const thumbImg = s.image_url?.trim();
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => goTo(i)}
                      aria-label={`Slide ${i + 1}`}
                      className="relative overflow-hidden rounded-lg flex-shrink-0 transition-all duration-300"
                      style={{
                        width: i === current ? 64 : 44,
                        height: i === current ? 40 : 44,
                        outline: i === current ? "2px solid rgba(255,255,255,0.8)" : "2px solid transparent",
                        outlineOffset: "2px",
                        ...(thumbImg ? {} : platformPromoSlideBackground(s.variant)),
                      }}
                    >
                      {thumbImg ? (
                        <Image src={thumbImg} alt="" fill sizes="64px" className="object-cover" />
                      ) : null}
                      {i !== current && (
                        <div className="absolute inset-0 bg-black/35 hover:bg-black/20 transition-colors" />
                      )}
                      {i === current && (
                        <div
                          className="absolute bottom-0 left-0 z-10 h-[2px] bg-[#E63946]"
                          style={{ width: `${progress}%`, transition: "width 0.08s linear" }}
                        />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {count > 1 && (
            <div className="sm:hidden flex items-center gap-1.5 flex-shrink-0 pointer-events-auto pb-0.5">
              {slides.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => goTo(i)}
                  className="h-1.5 rounded-full bg-white/40 transition-all duration-300"
                  style={{
                    width: i === current ? 18 : 6,
                    background: i === current ? "white" : undefined,
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {count > 1 && (
        <>
          <button
            type="button"
            onClick={prevSlide}
            aria-label="Previous"
            className="hidden xs:flex absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 z-30 w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-black/25 hover:bg-black/50 backdrop-blur-md border border-white/15 items-center justify-center text-white transition-all duration-200 hover:scale-110"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            type="button"
            onClick={nextSlide}
            aria-label="Next"
            className="hidden xs:flex absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 z-30 w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-black/25 hover:bg-black/50 backdrop-blur-md border border-white/15 items-center justify-center text-white transition-all duration-200 hover:scale-110"
          >
            <ChevronRight size={18} />
          </button>
        </>
      )}

      <div className="absolute top-4 right-4 z-30 text-white/35 text-[9px] font-semibold tracking-widest uppercase border border-white/12 px-2.5 py-1 rounded-full backdrop-blur-sm bg-black/10">
        {slide.isPaidAd && paidAdCornerLabel ? paidAdCornerLabel : cornerLabel}
      </div>

      {count > 1 && (
        <div className="absolute bottom-0 left-0 right-0 z-40 h-[2px] bg-white/10">
          <div className="h-full bg-[#E63946] transition-none" style={{ width: `${progress}%` }} />
        </div>
      )}

      <style>{`
        @keyframes platformSlideUp {
          from { opacity: 0; transform: translateY(18px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes platformHostGlowA {
          0%, 100% { opacity: 0.65; transform: translateX(0) scale(1); }
          50% { opacity: 1; transform: translateX(4%) scale(1.05); }
        }
        @keyframes platformHostGlowB {
          0%, 100% { opacity: 0.5; transform: translateY(0); }
          50% { opacity: 0.85; transform: translateY(-3%); }
        }
        @keyframes platformHostShineBar {
          0% { left: -45%; opacity: 0; }
          12% { opacity: 0.85; }
          88% { opacity: 0.85; }
          100% { left: 100%; opacity: 0; }
        }
        .platform-host-glow-a { animation: platformHostGlowA 7s ease-in-out infinite; }
        .platform-host-glow-b { animation: platformHostGlowB 5.5s ease-in-out infinite; }
        .platform-host-shine-bar { animation: platformHostShineBar 11s ease-in-out infinite; }
      `}</style>
    </div>
  );
}
